// ChatbotContext.jsx
import React, { createContext, useContext, useState, useRef, useEffect } from 'react';
import fetchMethodRequest from '../config/service';
import showToasterMessage from '../containers/UI/ToasterMessage/toasterMessage';
import apiCalls from '../config/apiCalls';

const ChatbotContext = createContext();

export const ChatbotProvider = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState([{ text: "Hello! I'm your AI assistant. How can I help?", sender: 'bot' }]);
  const [customerMessages, setCustomerMessages] = useState([{ text: "Hello! I'm your Customer Support. How can I help?", sender: 'bot' }]);
  const [inputValue, setInputValue] = useState('');
  const [chatId, setChatId] = useState();
  const [isLoading, setIsLoading] = useState(false);
  const [listening, setListening] = useState(false);
  const [activeSpeechMessageId, setActiveSpeechMessageId] = useState('');
  const [dimensions, setDimensions] = useState();
  const transcriptRef = useRef('');
  const [queryType, setQueryType] = useState(null);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);
  const [showChatbot, setShowChatbot] = useState(true);
  const errorText = "Sorry for the inconvenience. I’m having trouble responding right now — please try again later or reach out to the DMS management."
  const initialRenderRef = useRef(false);

  // Auto-scroll
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, customerMessages]);

  // Handle window size
  useEffect(() => {
    const updateHeight = () => {
      const innerHeight = window.innerHeight;
      const innerWidth = window.innerWidth;
      setDimensions({
        height: innerHeight * 0.7,
        width: innerWidth > 1000 ? innerWidth * 0.3 : innerWidth * 0.5,
      });
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, []);

  // Send data to server
  const sendDataToServer = async (message) => {
    if (!queryType) {
      showToasterMessage("No chatbot type selected, Please select it first", "error");
      return;
    }
    setIsLoading(true);
    const body = { query: message };
    let url = !chatId
      ? `${apiCalls.chatbot}?queryType=${queryType}`
      : `${apiCalls.chatbot}?chatId=${chatId}&queryType=${queryType}`;

    try {
      const response = await fetchMethodRequest("POST", url, body);
      if (response?.messages?.answer) {
        const answer = { text: response.messages.answer, sender: 'bot' };
        if (queryType === "dmsIssues") setCustomerMessages(prev => [...prev, answer]);
        else setMessages(prev => [...prev, answer]);
      }
      if (!chatId && response?.chatId) setChatId(response.chatId);
      if (response.errorCode) {
        setMessages(prev => [...prev, { text: errorText, sender: 'bot' }]);
        console.error(response.messageType || response.errorMessage || response.message || response);
      }
    } catch (err) {
      console.error(err);
      setMessages(prev => [...prev, { text: errorText, sender: 'bot' }]);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle send
  const handleSend = (e) => {
    e.preventDefault();
    if (!inputValue.trim()) return;
    sendDataToServer(inputValue);
    const newMsg = { text: inputValue, sender: 'user' };
    if (queryType === "dmsIssues") setCustomerMessages(prev => [...prev, newMsg]);
    else setMessages(prev => [...prev, newMsg]);
    setInputValue('');
  };

  const voiceTextSend = (textToSend) => {
    if (!textToSend.trim()) return;

    sendDataToServer(textToSend);
    const newMsg = { text: textToSend, sender: 'user' };

    if (queryType === "dmsIssues") {
      setCustomerMessages(prev => [...prev, newMsg]);
    } else {
      setMessages(prev => [...prev, newMsg]);
    }

    setInputValue('');
  };


  const handleActiveSpeechMessage = (messageId) => {
    setActiveSpeechMessageId(messageId);
  };

  const handleSupport = (type) => {
    if (type) setQueryType(type);
  };

  return (
    <ChatbotContext.Provider
      value={{
        isOpen, setIsOpen,
        messages, setMessages,
        customerMessages, setCustomerMessages,
        showChatbot, setShowChatbot,
        inputValue, setInputValue,
        chatId, setChatId,
        isLoading, setIsLoading,
        listening, setListening,
        activeSpeechMessageId, setActiveSpeechMessageId,
        dimensions,
        transcriptRef,
        queryType, setQueryType,
        messagesEndRef,
        sendDataToServer,
        handleSend,
        voiceTextSend,
        handleActiveSpeechMessage,
        handleSupport,
        inputRef,
        initialRenderRef,
      }}
    >
      {children}
    </ChatbotContext.Provider>
  );
};

export const useChatbot = () => useContext(ChatbotContext);
